/**
 * AttentionList.tsx — ONE merged "needs attention" list (v2.1 visual
 * redesign, 2026-08-05: four stacked queue cards were "too many items").
 *
 * Watch warnings pin to the top; queue items interleave beneath, oldest
 * first, capped — each with a tinted type icon, the story in one line, and
 * an age pill. Footer chips jump to each full queue.
 */
import { Box, Chip, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  FactCheckOutlined,
  Inbox,
  RateReviewOutlined,
  ScienceOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import type { DashboardData, QueueItem } from '../../types/dashboard';

const MAX_ROWS = 8;

interface Row {
  kind: 'approval' | 'intake' | 'review' | 'entry';
  item: QueueItem;
}

const KIND_STYLE: Record<Row['kind'], { icon: React.ReactNode; color: string; label: string }> = {
  approval: { icon: <FactCheckOutlined />, color: '#D4860A', label: 'Quote approval' },
  intake: { icon: <Inbox />, color: '#E65100', label: 'Client intake' },
  review: { icon: <RateReviewOutlined />, color: '#7B1FA2', label: 'Review' },
  entry: { icon: <ScienceOutlined />, color: '#4A6741', label: 'Data entry' },
};

export const AttentionList = ({ data }: { data: DashboardData }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const q = data.queues;

  const rows: Row[] = [
    ...(q.quoteApprovals?.items ?? []).map((item) => ({ kind: 'approval' as const, item })),
    ...(q.intake?.items ?? []).map((item) => ({ kind: 'intake' as const, item })),
    ...(q.review?.items ?? []).map((item) => ({ kind: 'review' as const, item })),
    ...(q.dataEntry?.items ?? []).map((item) => ({ kind: 'entry' as const, item })),
  ]
    .sort((a, b) => b.item.days - a.item.days)
    .slice(0, MAX_ROWS);

  const totals: Array<{ label: string; total: number; to: string }> = [
    ...(q.quoteApprovals?.total ? [{ label: `Approvals ${q.quoteApprovals.total}`, total: q.quoteApprovals.total, to: ROUTES.PENDING_APPROVALS }] : []),
    ...(q.intake?.total ? [{ label: `Intake ${q.intake.total}`, total: q.intake.total, to: ROUTES.INTAKE_REQUESTS }] : []),
    ...(q.review?.total ? [{ label: `Reviews ${q.review.total}`, total: q.review.total, to: `${ROUTES.TESTS}?status=Request Review,Review Completed` }] : []),
    ...(q.dataEntry?.total ? [{ label: `Data entry ${q.dataEntry.total}`, total: q.dataEntry.total, to: `${ROUTES.TESTS}?status=Scheduled` }] : []),
  ];

  return (
    <Paper elevation={0} sx={{ borderRadius: '18px', border: '1px solid', borderColor: 'border.color', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.25, pt: 1.75, pb: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800 }}>Needs attention</Typography>
        <Typography variant="caption" color="text.secondary">oldest first</Typography>
      </Box>

      {/* Watch warnings — pinned, tinted */}
      {data.watch.map((w, i) => (
        <Box
          key={`watch-${i}`}
          onClick={() => navigate(w.link)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, px: 2.25, py: 0.9, cursor: 'pointer',
            backgroundColor: alpha(w.severity === 'error' ? theme.palette.error.main : '#E65100', 0.06),
            borderTop: `1px solid ${alpha('#000', 0.04)}`,
            '&:hover': { backgroundColor: alpha(w.severity === 'error' ? theme.palette.error.main : '#E65100', 0.1) },
          }}
        >
          <WarningAmberOutlined sx={{ fontSize: 18, color: w.severity === 'error' ? theme.palette.error.main : '#E65100' }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: w.severity === 'error' ? theme.palette.error.main : '#B34700' }}>
            {w.text}
          </Typography>
        </Box>
      ))}

      {rows.length === 0 && data.watch.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Nothing needs attention</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
            New approvals, intake, reviews and scheduled tests will appear here.
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1 }}>
        {rows.map(({ kind, item }, i) => {
          const style = KIND_STYLE[kind];
          return (
            <Box
              key={`${kind}-${i}`}
              onClick={() => navigate(item.link)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1,
                cursor: 'pointer', borderTop: `1px solid ${alpha('#000', 0.04)}`,
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
              }}
            >
              <Box sx={{
                width: 34, height: 34, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: alpha(style.color, 0.12), color: style.color, flexShrink: 0, '& svg': { fontSize: 18 },
              }}>
                {style.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }} noWrap>
                  {item.id !== 'Intake' && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 700 }}>{item.id} · </Box>}
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }} noWrap>
                  {style.label}{item.meta ? ` — ${item.meta}` : ''}
                </Typography>
              </Box>
              {item.money != null && (
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: theme.palette.primary.main, flexShrink: 0 }}>
                  ${Number(item.money).toLocaleString()}
                </Typography>
              )}
              <Chip
                label={item.days > 0 ? `${item.days} d` : 'new'}
                size="small"
                sx={{
                  height: 20, fontSize: '0.66rem', fontWeight: 700, flexShrink: 0,
                  backgroundColor: item.days >= 5 ? alpha(theme.palette.error.main, 0.1) : alpha('#000', 0.05),
                  color: item.days >= 5 ? theme.palette.error.main : 'text.secondary',
                }}
              />
            </Box>
          );
        })}
      </Box>

      {totals.length > 0 && (
        <Box sx={{ px: 2.25, py: 1.1, display: 'flex', gap: 0.75, flexWrap: 'wrap', borderTop: `1px solid ${alpha('#000', 0.05)}`, backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
          {totals.map((t) => (
            <Chip
              key={t.label} label={`${t.label} →`} size="small" clickable
              onClick={() => navigate(t.to)}
              sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700, backgroundColor: alpha(theme.palette.primary.main, 0.07), color: theme.palette.primary.main }}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};
